import { useCallback, useMemo } from 'react';
import { toastService } from '../services/toastService';

interface UseToastReturn {
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  loading: (title: string, message?: string) => string;
  progress: (title: string, message?: string) => string;
  dismiss: (toastId?: string) => void;
  dismissAll: () => void;
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    }
  ) => Promise<T>;
  apiOperation: <T>(
    operation: () => Promise<T>,
    operationId: string,
    options?: {
      showLoading?: boolean;
      loadingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
    }
  ) => Promise<T>;
  handleApiError: (error: any, context?: string) => string;
  networkError: (message?: string, retryFn?: () => void) => string;
  validationError: (errors: string[] | string) => string;
  permissionDenied: (message?: string) => string;
  rateLimit: (message?: string) => string;
}

export const useToast = (): UseToastReturn => {
  const success = useCallback((title: string, message?: string) => {
    const fullMessage = message ? `${title}: ${message}` : title;
    return toastService.success(fullMessage);
  }, []);

  const error = useCallback((title: string, message?: string) => {
    const fullMessage = message ? `${title}: ${message}` : title;
    return toastService.error(fullMessage);
  }, []);

  const warning = useCallback((title: string, message?: string) => {
    const fullMessage = message ? `${title}: ${message}` : title;
    return toastService.warning(fullMessage);
  }, []);

  const info = useCallback((title: string, message?: string) => {
    const fullMessage = message ? `${title}: ${message}` : title;
    return toastService.info(fullMessage);
  }, []);

  const loading = useCallback((title: string, message?: string) => {
    const fullMessage = message ? `${title}: ${message}` : title;
    return toastService.loading(fullMessage);
  }, []);

  const progress = useCallback((title: string, message?: string) => {
    const fullMessage = message ? `${title}: ${message}` : title;
    return toastService.progress(fullMessage);
  }, []);

  const dismiss = useCallback((toastId?: string) => {
    toastService.dismiss(toastId);
  }, []);

  const dismissAll = useCallback(() => {
    toastService.dismissAll();
  }, []);

  const promise = useCallback(<T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string;
      error: string;
    }
  ) => {
    return toastService.promise(promise, messages);
  }, []);

  const apiOperation = useCallback(<T>(
    operation: () => Promise<T>,
    operationId: string,
    options?: {
      showLoading?: boolean;
      loadingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
    }
  ) => {
    return toastService.apiOperation(operation, operationId, options);
  }, []);

  const handleApiError = useCallback((error: any, context?: string) => {
    return toastService.handleApiError(error, context);
  }, []);

  const networkError = useCallback((message?: string, retryFn?: () => void) => {
    return toastService.networkError(message, retryFn);
  }, []);

  const validationError = useCallback((errors: string[] | string) => {
    return toastService.validationError(errors);
  }, []);

  const permissionDenied = useCallback((message?: string) => {
    return toastService.permissionDenied(message);
  }, []);

  const rateLimit = useCallback((message?: string) => {
    return toastService.rateLimit(message);
  }, []);

  return useMemo(() => ({
    success,
    error,
    warning,
    info,
    loading,
    progress,
    dismiss,
    dismissAll,
    promise,
    apiOperation,
    handleApiError,
    networkError,
    validationError,
    permissionDenied,
    rateLimit,
  }), [
    success,
    error,
    warning,
    info,
    loading,
    progress,
    dismiss,
    dismissAll,
    promise,
    apiOperation,
    handleApiError,
    networkError,
    validationError,
    permissionDenied,
    rateLimit,
  ]);
};

export default useToast;